// Copyright (c) Move Closer 2022.

import { IVersionResolver, PossibleVersion, SemanticVersion, VersionComparisonResult, VersionMode } from './contracts'
import { isFinite } from 'lodash'

/**
 * @author Łukasz Sitnicki <lukasz.sitnicki@movecloser.pl>
 */
export class VersionResolver implements IVersionResolver {
  protected version: PossibleVersion | null = null

  constructor (version: string, protected mode: VersionMode = VersionMode.SemVer) {
    this.setVersion(version)
  }

  /**
   * Compare given & set version. For semver you can set level of check: 1 - major, 2 - minor, 3 - patch
   * If return following values: 'different' | 'newer' | 'older' | 'same'
   */
  public compare (version: PossibleVersion, level: number = 3, strict: boolean = true): VersionComparisonResult {
    const isCandidateSemVer: boolean = this.isValidSemver(version)

    switch (this.mode) {
      case VersionMode.Noop:
        if (strict && isCandidateSemVer) {
          throw new Error('[VersionResolver]: Incompatible versions. Set "noop" but given "semver".')
        }

        return this.compareNoop(version as string)
      case VersionMode.SemVer:
      default:
        if (strict && !isCandidateSemVer) {
          throw new Error('[VersionResolver]: Incompatible versions. Set "semver" but given "noop".')
        }

        return this.compareSemver(version, level)
    }
  }

  public serialize (): string {
    return JSON.stringify({ v: this.version, m: this.mode })
  }

  public setMode (versionMode: VersionMode): void {
    this.mode = versionMode
  }

  public setVersion (version: string): void {
    switch (this.mode) {
      case VersionMode.Noop:
        this.version = this.parseNoop(version)
        break
      case VersionMode.SemVer:
      default:
        this.version = this.parseSemver(version)
        break
    }
  }

  public toString (): string {
    if (!this.version) {
      throw new Error('[VersionResolver]: Cannot parse unset version to string.')
    }

    return Array.isArray(this.version) ? this.version.join('.') : `${this.version}`
  }

  public static deserialize (source: string): PossibleVersion | null {
    try {
      const candidate = JSON.parse(source)

      return typeof candidate.v !== 'undefined' ? candidate.v : null
    } catch (e) {
      return null
    }
  }

  protected compareNoop (version: string): VersionComparisonResult {
    return this.version === version ? VersionComparisonResult.Same : VersionComparisonResult.Different
  }

  protected compareSemver (candidate: PossibleVersion, level: number): VersionComparisonResult {
    if (!this.version) {
      throw new Error('[VersionResolver]: Cannot compare unset version.')
    }

    if (!this.isValidSemver(candidate)) {
      throw new Error(`[VersionResolver]: Cannot compare versions. Given one [${candidate}] is not a valid semver.`)
    }

    const { major, minor, patch, modifier } = this.version as SemanticVersion
    const { major: oldMajor, minor: oldMinor, patch: oldPatch, modifier: oldModifier } =
      typeof candidate === 'string' ? this.parseSemver(candidate) : candidate

    const majorDiff = major - oldMajor
    const minorDiff = minor - oldMinor
    const patchDiff = patch - oldPatch

    if (majorDiff !== 0) {
      return majorDiff > 0 ? VersionComparisonResult.Newer : VersionComparisonResult.Older
    }

    if (minorDiff !== 0 && level > 1) {
      return minorDiff > 0 ? VersionComparisonResult.Newer : VersionComparisonResult.Older
    }

    if (patchDiff !== 0 && level > 2) {
      return patchDiff > 0 ? VersionComparisonResult.Newer : VersionComparisonResult.Older
    }

    if (modifier !== oldModifier && level > 3) {
      return VersionComparisonResult.Different
    }

    return VersionComparisonResult.Same
  }

  protected isValidSemver (candidate: PossibleVersion): boolean {
    if (typeof candidate === 'object') {
      return candidate !== null
    }

    return candidate.split('.')
      .reduce<number>((v, p) => {
        v = isFinite(parseInt(p)) ? v + 1 : v - 3
        return v
      }, 0) >= 2
  }

  protected parseNoop (version: string): string {
    return version
  }

  protected parseSemver (rawVersion: string): SemanticVersion {
    const parsedVersion: SemanticVersion = { major: 1, minor: 0, patch: 0 }
    const parts = rawVersion.toLowerCase().replace(/^v/, '').split('-')
    const version = parts[0].split('.')

    if (version.length < 2) {
      return parsedVersion
    }

    const majorCandidate = parseInt(version[0])
    if (isFinite(majorCandidate)) {
      parsedVersion.major = majorCandidate
    }

    const minorCandidate = parseInt(version[1])
    if (isFinite(minorCandidate)) {
      parsedVersion.minor = minorCandidate
    }

    const patchCandidate = parseInt(version[2])
    if (isFinite(patchCandidate)) {
      parsedVersion.patch = patchCandidate
    }

    if (parts.length > 1) {
      parsedVersion.modifier = parts[1]
    }

    return parsedVersion
  }
}
